import api from "./api";

/**
 * Authentication Service
 *
 * Handles cookie-based authentication with the ERP backend.
 * Uses httpOnly cookies for secure token storage.
 */

export interface LoginRequest {
  username: string;
  password: string;
}

export interface AuthUser {
  user_id: string;
  email: string;
  role: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  permissions: string[];
}

export interface AuthStatusResponse {
  authenticated: boolean;
  user_id: string;
  email: string;
  role: string;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  permissions: string[];
}

export interface AuthStatus {
  authenticated: boolean;
  user?: AuthUser;
}

const auth_base = "/auth";
export const authService = {
  /**
   * Login with email and password
   * POST /auth/jwt/login
   * Uses form-data format required by FastAPI OAuth2
   */
  login: async (
    email: string,
    password: string,
  ): Promise<{ message: string }> => {
    try {
      const formData = new URLSearchParams();
      formData.append("username", email); // FastAPI OAuth2 uses 'username' field
      formData.append("password", password);

      const response = await api.post(`${auth_base}/jwt/login`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      // Cookies are automatically set by the browser
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  },

  /**
   * Logout and clear authentication cookies
   * POST /auth/jwt/logout
   */
  logout: async (): Promise<{ message: string }> => {
    try {
      const response = await api.post(`${auth_base}/jwt/logout`);
      // Cookies are automatically cleared by the backend
      console.log("LOGOUT RESPONSE:", response);
      return response.data;
    } catch (error: any) {
      // Even if logout fails, we should clear local state
      console.warn(
        "Logout request failed:",
        error.response?.data?.detail || error.message,
      );
      return { message: "Logged out locally" };
    }
  },

  /**
   * Check current authentication status
   * GET /auth/status - Returns basic user information if authenticated
   */
  checkAuthStatus: async (): Promise<AuthStatus> => {
    try {
      const response = await api.get("/auth/status");
      return {
        authenticated: true,
        user: response.data,
      };
    } catch (error: any) {
      if (error.response?.status === 401) {
        return { authenticated: false };
      }
      // Handle network/CORS errors gracefully
      if (error.code === "ERR_NETWORK") {
        console.warn(
          "Network error during auth check - treating as not authenticated",
        );
        return { authenticated: false };
      }
      throw error;
    }
  },

  /**
   * Refresh authentication token
   * POST /auth/refresh
   */
  refreshToken: async (): Promise<{ message: string }> => {
    try {
      const response = await api.post("/auth/refresh");
      // New tokens are automatically set as cookies
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || "Token refresh failed");
    }
  },

  forgotPassword: async (email: string): Promise<any> => {
    try {
      const response = await api.post("/auth/reset-password/forgot-password", {
        email: email,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Forgot Password Request Failed",
      );
    }
  },

  resetPassword: async (token: string, password: string): Promise<any> => {
    try {
      const response = await api.post("/auth/reset-password/reset-password", {
        token: token,
        password: password,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail || "Reset Password Request Failed",
      );
    }
  },
};
